/**
 * Edit Mode Context and Components
 * 
 * Provides edit mode functionality for the dev preview app.
 * Allows component-level selection and editing.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import type { ComponentSlot } from '../types';
import type { ComponentRequirements } from '@vibesites/tokens';

// ============================================================================
// Types
// ============================================================================

/**
 * Information about a selected component for editing.
 */
export interface ComponentEditInfo {
  /** Component slot name from registry (e.g., 'AmenitiesList') */
  slot: ComponentSlot;
  /** Section ID from page composition */
  sectionId: string;
  /** Props passed to the section */
  sectionProps?: Record<string, unknown>;
  /** Component requirements (design tokens, data tokens) */
  requirements?: ComponentRequirements;
  /** DOM bounding rect of the component */
  bounds?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  /** 
   * Component's self-declared slot from contentSchema.slot
   * May differ from registry slot (e.g., registry has 'AmenitiesList' but 
   * component declares 'AmenitiesSection'). Use this for file naming.
   */
  schemaSlot?: string;
}

/**
 * `VIBESITE_SELECT_COMPONENT` message payload — a component to open plus a
 * transient token path the parent editor should scroll/highlight on open.
 */
export interface ComponentSelectPayload extends ComponentEditInfo {
  highlightTokenPath?: string;
}

/**
 * Bounding rectangle for positioning inline editors.
 */
export interface TokenBounds {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

/**
 * Information about a selected token for editing.
 */
export interface TokenEditInfo {
  /** Token path (e.g., 'hero.title') */
  path: string;
  /** Bounding rect for positioning */
  bounds?: TokenBounds;
}

/**
 * Edit mode context value.
 */
export interface EditModeContextValue {
  /** Whether edit mode is active */
  isEditMode: boolean;
  /** Toggle edit mode on/off */
  setEditMode: (enabled: boolean) => void;
  /** Currently selected component (null if none) */
  selectedComponent: ComponentEditInfo | null;
  /** Select a component for editing (optional highlightTokenPath deep-links a panel section) */
  selectComponent: (info: ComponentSelectPayload) => void;
  /** Deselect the current component */
  deselectComponent: () => void;
  /** Currently hovered component slot */
  hoveredSlot: ComponentSlot | null;
  /** Set hovered slot */
  setHoveredSlot: (slot: ComponentSlot | null) => void;
  
  // Token-level selection (Phase 1 granular editing)
  /** Currently selected token path (null if none) */
  selectedToken: string | null;
  /** Full info about selected token including bounds */
  selectedTokenInfo: TokenEditInfo | null;
  /** Select a token for editing */
  selectToken: (path: string, bounds?: TokenBounds) => void;
  /** Deselect the current token */
  deselectToken: () => void;
  /** Currently hovered token path */
  hoveredToken: string | null;
  /** Set hovered token */
  setHoveredToken: (path: string | null) => void;
  
  // Inline editing mode
  /** Token path currently being inline edited (shows popover) */
  inlineEditingToken: string | null;
  /** Start inline editing a token */
  startInlineEdit: (path: string, bounds: TokenBounds) => void;
  /** End inline editing */
  endInlineEdit: () => void;

  // Scope mode — parent routes clicks into the AI composer as chips instead
  // of opening component/token/slot-group editors.
  scopeMode: boolean;
  setScopeMode: (enabled: boolean) => void;

  /** Comment-pin mode — same click targets as scope mode, but posts to site-comments flow. */
  commentPinMode: boolean;
  setCommentPinMode: (enabled: boolean) => void;
}

// ============================================================================
// Context
// ============================================================================

const EditModeContext = createContext<EditModeContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

export interface EditModeProviderProps {
  /** Initial edit mode state */
  initialEditMode?: boolean;
  /**
   * Callback when a component is selected. The provider already posts
   * VIBESITE_COMPONENT_SELECTED to the parent frame — don't re-post from here.
   */
  onComponentSelect?: (info: ComponentEditInfo) => void;
  /** Callback when component is deselected */
  onComponentDeselect?: () => void;
  /** Children components */
  children: ReactNode;
}

/**
 * Provider component for edit mode functionality.
 * Wrap your app or preview frame with this to enable edit mode.
 */
export function EditModeProvider({
  initialEditMode = false,
  onComponentSelect,
  onComponentDeselect,
  children,
}: EditModeProviderProps) {
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [selectedComponent, setSelectedComponent] = useState<ComponentEditInfo | null>(null);
  const [hoveredSlot, setHoveredSlot] = useState<ComponentSlot | null>(null);
  
  // Token-level selection state (Phase 1 granular editing)
  const [selectedToken, setSelectedToken] = useState<string | null>(null);
  const [selectedTokenInfo, setSelectedTokenInfo] = useState<TokenEditInfo | null>(null);
  const [hoveredToken, setHoveredToken] = useState<string | null>(null);
  
  // Inline editing state
  const [inlineEditingToken, setInlineEditingToken] = useState<string | null>(null);
  const [_inlineEditingBounds, setInlineEditingBounds] = useState<TokenBounds | undefined>(undefined);

  // Scope mode state — synced from parent via VIBESITE_SCOPE_MODE_TOGGLE.
  const [scopeMode, setScopeMode] = useState(false);

  // Site-comments pin mode — synced from parent via VIBESITE_COMMENT_PIN_MODE_TOGGLE.
  const [commentPinMode, setCommentPinMode] = useState(false);

  // Handle edit mode toggle
  const setEditMode = useCallback((enabled: boolean) => {
    setIsEditMode(enabled);
    if (!enabled) {
      // Clear all selection when exiting edit mode
      setSelectedComponent(null);
      setHoveredSlot(null);
      setSelectedToken(null);
      setSelectedTokenInfo(null);
      setHoveredToken(null);
      setInlineEditingToken(null);
      setInlineEditingBounds(undefined);
    }
  }, []);

  // Handle component selection. The parent post happens here (not in an effect
  // on selectedComponent) so the transient highlightTokenPath deep-link rides
  // the payload exactly once — StrictMode re-runs effects, not handlers.
  const selectComponent = useCallback((info: ComponentSelectPayload) => {
    const { highlightTokenPath: _highlightTokenPath, ...component } = info;
    setSelectedComponent(component);
    // Clear token selection when selecting a component
    setSelectedToken(null);
    setSelectedTokenInfo(null);
    onComponentSelect?.(component);
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'VIBESITE_COMPONENT_SELECTED',
        payload: info,
      }, '*');
    }
  }, [onComponentSelect]);

  // Handle component deselection
  const deselectComponent = useCallback(() => {
    setSelectedComponent(null);
    setSelectedToken(null);
    setSelectedTokenInfo(null);
    onComponentDeselect?.();
  }, [onComponentDeselect]);

  // Handle token selection
  const selectToken = useCallback((path: string, bounds?: TokenBounds) => {
    setSelectedToken(path);
    setSelectedTokenInfo({ path, bounds });
    
    // Notify parent frame about token selection
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'VIBESITE_TOKEN_SELECTED',
        payload: { path, bounds },
      }, '*');
    }
  }, []);

  // Handle token deselection
  const deselectToken = useCallback(() => {
    setSelectedToken(null);
    setSelectedTokenInfo(null);
    
    // Notify parent frame
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'VIBESITE_TOKEN_DESELECTED',
      }, '*');
    }
  }, []);

  // Start inline editing a token — notify parent to render the editor overlay
  const startInlineEdit = useCallback((path: string, bounds: TokenBounds) => {
    setInlineEditingToken(path);
    setInlineEditingBounds(bounds);
  }, []);

  // End inline editing (called by parent via VIBESITE_END_INLINE_EDIT message)
  const endInlineEdit = useCallback(() => {
    setInlineEditingToken(null);
    setInlineEditingBounds(undefined);
  }, []);

  // Listen for postMessage from parent frame (for iframe usage)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle edit mode toggle from parent
      if (event.data?.type === 'VIBESITE_EDIT_MODE_TOGGLE') {
        setEditMode(event.data.enabled);
      }
      // Handle deselect from parent
      if (event.data?.type === 'VIBESITE_DESELECT_COMPONENT') {
        deselectComponent();
      }
      // Parent inline editor requests selecting the owning section (e.g. breadcrumb click).
      if (event.data?.type === 'VIBESITE_SELECT_COMPONENT') {
        const info = event.data.payload as ComponentSelectPayload;
        const sectionEl = document.querySelector(
          `[data-vibesite-slot="${info.slot}"][data-vibesite-section-id="${info.sectionId}"]`,
        );
        const rect = sectionEl?.getBoundingClientRect();
        const bounds = rect
          ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
          : info.bounds;
        endInlineEdit();
        selectComponent({ ...info, bounds });
      }
      // Parent component panel — scroll to token/slot group and open inline editor.
      if (event.data?.type === 'VIBESITE_OPEN_INLINE_EDIT') {
        const payload = event.data.payload || {};
        const sectionId = typeof payload.sectionId === 'string' ? payload.sectionId : '';
        const tokenPath = typeof payload.tokenPath === 'string' ? payload.tokenPath : '';
        const groupPath = typeof payload.groupPath === 'string' ? payload.groupPath : '';
        const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape : (s: string) => s.replace(/"/g, '\\"');

        const findTarget = (root: ParentNode): HTMLElement | null => {
          if (tokenPath) {
            return root.querySelector(`[data-vibesite-token="${esc(tokenPath)}"]`) as HTMLElement | null;
          }
          if (groupPath) {
            return root.querySelector(`[data-vibesite-slot-group="${esc(groupPath)}"]`) as HTMLElement | null;
          }
          return null;
        };

        let target: HTMLElement | null = null;
        if (sectionId) {
          const sectionEl = document.querySelector(`[data-vibesite-section-id="${esc(sectionId)}"]`);
          if (sectionEl) target = findTarget(sectionEl);
        }
        if (!target) target = findTarget(document);
        if (!target) return;

        target.scrollIntoView({ behavior: 'instant', block: 'center' });
        target.click();
      }
      // Handle token deselect from parent
      if (event.data?.type === 'VIBESITE_DESELECT_TOKEN') {
        deselectToken();
      }
      // Handle scope mode toggle from parent
      if (event.data?.type === 'VIBESITE_SCOPE_MODE_TOGGLE') {
        setScopeMode(event.data.enabled);
      }
      if (event.data?.type === 'VIBESITE_COMMENT_PIN_MODE_TOGGLE') {
        setCommentPinMode(!!event.data.enabled);
      }
      // Handle end inline edit from parent
      if (event.data?.type === 'VIBESITE_END_INLINE_EDIT') {
        endInlineEdit();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setEditMode, deselectComponent, deselectToken, endInlineEdit, setScopeMode, setCommentPinMode, selectComponent]);

  // Escape closes the component editor. Keydowns inside the iframe never reach
  // the parent document, so the host's Radix Dialog can't catch Escape itself
  // when focus is still on the selected component (the common case, since
  // opening the editor doesn't steal focus from the iframe).
  useEffect(() => {
    if (!selectedComponent) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') deselectComponent();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedComponent, deselectComponent]);

  const contextValue: EditModeContextValue = {
    isEditMode,
    setEditMode,
    selectedComponent,
    selectComponent,
    deselectComponent,
    hoveredSlot,
    setHoveredSlot,
    // Token-level selection
    selectedToken,
    selectedTokenInfo,
    selectToken,
    deselectToken,
    hoveredToken,
    setHoveredToken,
    // Inline editing
    inlineEditingToken,
    startInlineEdit,
    endInlineEdit,
    // Scope mode
    scopeMode,
    setScopeMode,
    commentPinMode,
    setCommentPinMode,
  };

  return (
    <EditModeContext.Provider value={contextValue}>
      {/* Add edit mode class to enable CSS styling */}
      <div 
        className={isEditMode ? 'vibesite-edit-mode-active' : ''}
        data-edit-mode={isEditMode}
        data-vibesite-scope-mode={scopeMode}
        data-vibesite-comment-pin-mode={commentPinMode}
      >
        {children}
      </div>
    </EditModeContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access edit mode context.
 * Returns null if used outside of EditModeProvider.
 */
export function useEditMode(): EditModeContextValue | null {
  return useContext(EditModeContext);
}

/**
 * Hook to access edit mode context (throws if not available).
 */
export function useEditModeRequired(): EditModeContextValue {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditModeRequired must be used within an EditModeProvider');
  }
  return context;
}

// ============================================================================
// Cross-Frame Inline Editor Messages
// ============================================================================

/**
 * Request the parent frame to open an inline token editor overlay.
 * The parent renders the editor UI — the iframe just provides the data.
 */
export function requestInlineTokenEditor(payload: {
  path: string;
  definition: Record<string, unknown>;
  currentValue: string | null;
  mode: 'field' | 'custom' | 'generated';
  currentField: string | null;
  anchorBounds: TokenBounds;
  componentInfo?: ComponentEditInfo | null;
}) {
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'VIBESITE_INLINE_EDIT_OPEN_TOKEN', payload }, '*');
  }
}

/**
 * Request the parent frame to open an inline slot group editor overlay.
 */
export function requestInlineSlotGroupEditor(payload: {
  groupPath: string;
  definition: Record<string, unknown>;
  override: unknown;
  anchorBounds: TokenBounds;
  componentInfo?: ComponentEditInfo | null;
}) {
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'VIBESITE_INLINE_EDIT_OPEN_SLOT_GROUP', payload }, '*');
  }
}

// ============================================================================
// Scope Mode Outbound Event
// ============================================================================

/**
 * Payload for a scope-mode target selection. The parent builds the appropriate
 * chip (section / token / slot group) and inserts it into the AI composer.
 */
export interface ScopeTargetPayload {
  kind: 'section' | 'token' | 'slotGroup';
  componentInfo: ComponentEditInfo | null;
  tokenPath?: string;
  groupPath?: string;
  /** Token type from schema (e.g. 'text' | 'block' | 'image' | ...). */
  tokenType?: string;
  /** Live resolved value for the token — for blocks this is the blockId. */
  tokenValue?: string | null;
}

/**
 * Notify the parent that a scope target was clicked while scope mode is on.
 * Each editable primitive calls this in place of its normal edit flow.
 */
export function postScopeTargetSelected(payload: ScopeTargetPayload) {
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'VIBESITE_SCOPE_TARGET_SELECTED', payload }, '*');
  }
}

/**
 * Same payload shape as scope mode; parent listens for VIBESITE_COMMENT_PIN_TARGET_SELECTED.
 */
export function postCommentPinTargetSelected(payload: ScopeTargetPayload) {
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'VIBESITE_COMMENT_PIN_TARGET_SELECTED', payload }, '*');
  }
}

